import { RootState } from '../../app/store';
import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import { apiResponseHelper, replaceDashboardWidgets, setDashboardWidgets } from './helpers';
import { isNullOrUndefined } from '@fiji/common/src/utils/helpers';
import { DEFAULT_WIDGET_LAYOUT } from '@fiji/common/src/utils/constants';

const intitialState = {
    widgetList: [],
    widgetDetails: {},
    dashboardWidgetIds: [],
    dashboardId: undefined,
    layout: [],
    loaders: {},
    apiResponse: [],
    removedWidgets: [],
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: intitialState,
    reducers: {
        resetLocalDashboard: () => {
            return intitialState;
        },
        resetLoaders: (state: RootState) => {
            state.loaders = {};
        },
        setLayout: (state: RootState, action: PayloadAction<any>) => {
            state.layout = action.payload;
        },
        setApiResponse: (state: RootState, action: PayloadAction<any>) => {
            apiResponseHelper(state, action);
        },
        setDashboardId: (state: RootState, action: PayloadAction<any>) => {
            state.dashboardId = action.payload;
        },
        setWidgetList: (state: RootState, action: PayloadAction<any>) => {
            setDashboardWidgets(state, action);
        },
        replaceWidgetList: (state: RootState, action: PayloadAction<any>) => {
            replaceDashboardWidgets(state, action);
        },
        setDashboardWidgetIds: (state: RootState, action: PayloadAction<any>) => {
            const currentClone = current(state);
            const selectedId = currentClone?.dashboardWidgetIds?.findIndex((item: any) => item === action.payload);
            if (selectedId === -1) {
                state.dashboardWidgetIds.push(action.payload);
            } else {
                state.dashboardWidgetIds?.splice(selectedId, 1);
            }
        },

        configLayout: (state: RootState, action: PayloadAction<any>) => {
            const stateClone = current(state);
            const filteredWidgets = stateClone?.widgetList?.filter((item: any) => !item.config?.isPinned) || [];

            // If no widgets, reset layout
            if (filteredWidgets.length === 0) {
                state.layout = [];
                return;
            }

            // Create a map of existing layout positions by widget ID for quick lookup
            const existingLayoutMap = new Map();
            const currentLayout = stateClone.layout || [];
            currentLayout.forEach((layoutItem: any) => {
                existingLayoutMap.set(layoutItem.i, layoutItem);
            });

            // Create a map of server layout positions by widget ID
            const serverLayoutMap = new Map();
            if (action?.payload?.widgetLayout) {
                action.payload.widgetLayout.forEach((layoutItem: any) => {
                    serverLayoutMap.set(layoutItem.i, layoutItem);
                });
            }

            const layoutClone: any = [];
            let maxY = 0;

            filteredWidgets.forEach((widget: any, widgetIndex: number) => {
                const widgetId = widget?.id;
                const existingLayout = existingLayoutMap.get(widgetId);
                const serverLayout = serverLayoutMap.get(widgetId);

                let layoutItem: any;

                // Priority: 1. Existing layout (preserve current positions)
                // 2. Server layout (from dashboard data)
                // 3. Create new layout for new widgets
                if (existingLayout) {
                    // Preserve existing position - this prevents reordering during edits
                    layoutItem = {
                        ...existingLayout,
                        minH: 4,
                        minW: 14,
                    };
                } else if (serverLayout) {
                    // Use server layout for widgets restored from database
                    layoutItem = {
                        ...serverLayout,
                        minH: 4,
                        minW: 14,
                    };
                } else {
                    // Create new layout for new widgets
                    const defaultLayout =
                        DEFAULT_WIDGET_LAYOUT[widget?.widgetType?.id] || DEFAULT_WIDGET_LAYOUT['common'];

                    // Calculate position for new widget to avoid overlaps
                    const newX = 0; // Start from left
                    const newY = maxY; // Place below existing widgets

                    layoutItem = {
                        i: widgetId,
                        ...defaultLayout,
                        x: newX,
                        y: newY,
                        minH: 4,
                        minW: 14,
                    };
                }

                layoutClone[widgetIndex] = layoutItem;

                // Track the maximum Y position for placing new widgets
                const bottomY = layoutItem.y + layoutItem.h;
                if (bottomY > maxY) {
                    maxY = bottomY;
                }
            });

            // Only update layout if there are actual changes to prevent unnecessary re-renders
            const hasChanges =
                layoutClone.length !== currentLayout.length ||
                layoutClone.some((item: any, index: number) => {
                    const currentItem = currentLayout[index];
                    return (
                        !currentItem ||
                        item.i !== currentItem.i ||
                        item.x !== currentItem.x ||
                        item.y !== currentItem.y ||
                        item.w !== currentItem.w ||
                        item.h !== currentItem.h
                    );
                });

            if (hasChanges) {
                state.layout = layoutClone;
            }
        },
        removeWidget: (state: RootState, action: PayloadAction<any>) => {
            const widgetsClone = current(state);
            const selectedIndex = widgetsClone?.widgetList?.findIndex((item: any) => item?.id === action.payload);
            const selectedWidgetId = widgetsClone?.dashboardWidgetIds?.indexOf(action.payload);
            const removedWidget = widgetsClone?.removedWidgets?.indexOf(action.payload);

            state.widgetList?.splice(selectedIndex, 1);
            if (selectedWidgetId !== -1) {
                state.dashboardWidgetIds?.splice(selectedWidgetId, 1);
            }
            if (removedWidget === -1) {
                state.removedWidgets.push(action.payload);
            }
        },
        // Drops the cached copy of a widget (details, api response, loader) without marking it as
        // removed from the dashboard, so it is re-fetched on the next render of the edit dashboard.
        refreshWidget: (state: RootState, action: PayloadAction<any>) => {
            const stateClone = current(state);
            const widgetId = action.payload;

            const widgetIndex = stateClone?.widgetList?.findIndex((item: any) => item?.id === widgetId) ?? -1;
            if (widgetIndex > -1) {
                state.widgetList.splice(widgetIndex, 1);
            }

            const responseIndex = stateClone?.apiResponse?.findIndex((item: any) => item?.widgetId === widgetId) ?? -1;
            if (responseIndex > -1) {
                state.apiResponse.splice(responseIndex, 1);
            }

            delete state.loaders[widgetId];
        },
        configureValues: (state: RootState, action: PayloadAction<any>) => {
            const stateClone = current(state);

            const selectedIndex = stateClone?.widgetList?.findIndex(
                (item: any) => item?.id === action?.payload?.widgetId
            );
            if (!isNullOrUndefined(selectedIndex) && selectedIndex !== -1 && action?.payload?.config) {
                state['widgetList'][selectedIndex]['config'] = action.payload?.config;
            }
        },
        setWidgetDetailsLoader: (state: RootState, action: PayloadAction<any>) => {
            state['loaders'][action.payload.key] = action.payload.value;
        },
        removeWidgetLoadingState: (state: RootState, action: PayloadAction<any>) => {
            delete state['loaders'][action.payload];
        },
    },
});

export const {
    setLayout,
    removeWidget,
    refreshWidget,
    resetLoaders,
    configLayout,
    setWidgetList,
    setDashboardId,
    setApiResponse,
    configureValues,
    resetLocalDashboard,
    setDashboardWidgetIds,
    setWidgetDetailsLoader,
    removeWidgetLoadingState,
    replaceWidgetList,
} = dashboardSlice.actions;

export default dashboardSlice;
